import React, { useEffect, useMemo, useState } from "react";
import supabase from "../../../../conndb";
import NavabarGest from "./navabarGes";
import Loader from "./loader";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function ClientiConScadenza() {
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const formatDateIT = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("it-IT");
  };

  const meseCorrenteLabel = () => {
    const d = new Date();
    d.setDate(1);
    // ✅ NON aggiungiamo +1: mese corrente
    return d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  };

  const fetchClienti = async () => {
    setLoading(true);
    setError("");

    // ✅ RIFERIMENTO: MESE CORRENTE (non mese successivo)
    const ref = new Date();
    ref.setDate(1); // stabilizza per evitare problemi di fine mese
    const meseRif = ref.getMonth() + 1;
    const annoRif = ref.getFullYear();

    try {
      const { data: clientiData, error: clientiError } = await supabase
        .from("clienti")
        .select("*, caldaie(*), stufe(*)");
      if (clientiError) throw clientiError;

      const { data: interventiCaldaie } = await supabase.from("interventi_caldaie").select("*");
      const { data: interventiStufe } = await supabase.from("interventi_stufe").select("*");

      const clientiFiltrati = (clientiData || [])
        .map((cliente) => {
          // ---- CALDAIE ---- (logica tua: biennale)
          const caldaieValide = (cliente.caldaie || []).filter((c) => {
            const interventi = (interventiCaldaie || []).filter(
              (i) => i.cliente_id === cliente.id && i.caldaia_id === c.id
            );

            // Se c'è già un intervento nell'anno di riferimento -> non valido
            if (interventi.some((i) => new Date(i.data).getFullYear() === annoRif)) return false;

            // Se c'è un intervento 2 anni fa nello stesso mese -> valido
            if (
              interventi.some((i) => {
                const d = new Date(i.data);
                return d.getFullYear() === annoRif - 2 && d.getMonth() + 1 === meseRif;
              })
            )
              return true;

            // Altrimenti usa la data scadenza/installazione
            const dataInstall = new Date(c.scadenza);
            return (
              dataInstall.getFullYear() === annoRif - 2 &&
              dataInstall.getMonth() + 1 === meseRif
            );
          });

          // ---- STUFE ---- (logica tua: annuale)
          const stufeValide = (cliente.stufe || []).filter((s) => {
            const interventi = (interventiStufe || []).filter(
              (i) => i.cliente_id === cliente.id && i.stufa_id === s.id
            );

            // Se c'è già un intervento nell'anno di riferimento -> non valido
            if (interventi.some((i) => new Date(i.data).getFullYear() === annoRif)) return false;

            // Se c'è un intervento 1 anno fa nello stesso mese -> valido
            if (
              interventi.some((i) => {
                const d = new Date(i.data);
                return d.getFullYear() === annoRif - 1 && d.getMonth() + 1 === meseRif;
              })
            )
              return true;

            // Altrimenti usa la data scadenza/installazione
            const dataInstall = new Date(s.scadenza);
            return (
              dataInstall.getFullYear() === annoRif - 1 &&
              dataInstall.getMonth() + 1 === meseRif
            );
          });

          const caldaieConUltimo = caldaieValide.map((c) => {
            const interventi = (interventiCaldaie || []).filter(
              (i) => i.cliente_id === cliente.id && i.caldaia_id === c.id
            );
            const ultimo = interventi.sort((a, b) => new Date(b.data) - new Date(a.data))[0];
            return { ...c, ultimoIntervento: ultimo || null, tipo: "caldaia" };
          });

          const stufeConUltimo = stufeValide.map((s) => {
            const interventi = (interventiStufe || []).filter(
              (i) => i.cliente_id === cliente.id && i.stufa_id === s.id
            );
            const ultimo = interventi.sort((a, b) => new Date(b.data) - new Date(a.data))[0];
            return { ...s, ultimoIntervento: ultimo || null, tipo: "stufa" };
          });

          return { ...cliente, caldaieValide: caldaieConUltimo, stufeValide: stufeConUltimo };
        })
        .filter((c) => (c.caldaieValide?.length || 0) > 0 || (c.stufeValide?.length || 0) > 0);

      setClienti(clientiFiltrati);
    } catch (err) {
      console.error(err);
      setError("Errore durante il recupero dei clienti");
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchClienti();
  }, []);

  // Lista eventi (tabella + mobile + pdf)
  const eventi = useMemo(() => {
    const out =
      clienti
        ?.flatMap((c) =>
          [...(c.caldaieValide || []), ...(c.stufeValide || [])].map((item) => ({
            data: item?.scadenza,
            cliente: `${c?.nome || ""} ${c?.cognome || ""}`.trim(),
            nome: c?.nome || "",
            cognome: c?.cognome || "",
            dispositivo: item?.tipo || "",
            matricola: item?.matricola || "",
            modello: item?.modello || "",
            ultimo: item?.ultimoIntervento?.data || "",
            indirizzo: c?.indirizzo || "",
            telefono: c?.telefono || "",
          }))
        )
        ?.sort((a, b) => new Date(a.data) - new Date(b.data)) || [];

    return out;
  }, [clienti]);

  const exportClientiPDF = () => {
    try {
      if (!eventi?.length) {
        setToast({ type: "info", msg: "Nessun evento da esportare con i filtri attuali." });
        return;
      }

      const labelMese = meseCorrenteLabel();

      const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const marginX = 14;
      let y = 16;

      doc.setFontSize(16);
      doc.text(`Eventi del mese: ${labelMese}`, marginX, y);
      y += 6;

      autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        theme: "grid",
        head: [[
          "Data",
          "Cliente",
          "Dispositivo",
          "Matricola",
          "Modello",
          "Ultimo intervento",
          "Indirizzo",
          "Telefono",
        ]],
        body: eventi.map((e) => [
          formatDateIT(e.data),
          e.cliente,
          e.dispositivo,
          e.matricola,
          e.modello,
          formatDateIT(e.ultimo),
          e.indirizzo,
          e.telefono,
        ]),
        styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
        headStyles: { fontSize: 8 },
      });

      const safeLabel = labelMese.replace(/\s+/g, "_").replace(/[^\w-]/g, "");
      doc.save(`eventi_${safeLabel}.pdf`);

      setToast({ type: "success", msg: "PDF generato con successo." });
    } catch (e) {
      console.error(e);
      setToast({ type: "error", msg: `Errore durante l'esportazione PDF: ${e?.message || e}` });
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="p-4 md:p-6 min-h-screen">
      <NavabarGest />

      <div className="bg-[#5b90ab70] p-4 rounded-2xl">
        <h1 className="md:text-3xl font-bold mb-4 text-center text-xl text-white">
          Scadenze del mese corrente
        </h1>

        {error && <div className="mb-4 rounded-lg bg-red-600 text-white px-4 py-2">{error}</div>}

        {toast && (
          <div
            className={`mb-4 rounded-lg px-4 py-2 ${
              toast.type === "success"
                ? "bg-green-600 text-white"
                : toast.type === "info"
                ? "bg-blue-600 text-white"
                : "bg-red-600 text-white"
            }`}
          >
            {toast.msg}
          </div>
        )}

        {/* Export */}
        <div className="mb-4 flex items-center gap-3">
          <button
            onClick={exportClientiPDF}
            disabled={!eventi.length}
            className={`${
              !eventi.length ? "bg-gray-400 cursor-not-allowed" : "bg-amber-600 hover:bg-amber-700"
            } text-white px-4 py-2 rounded-lg`}
          >
            Esporta clienti (PDF)
          </button>

          <div className="text-white/90 text-sm">
            Eventi trovati: <span className="font-bold">{eventi.length}</span>
          </div>
        </div>

        {/* DESKTOP/TABLET */}
        <div className="hidden md:block">
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-white bg-[#173341] rounded-2xl overflow-hidden">
              <thead className="bg-[#0f2430]">
                <tr className="text-left">
                  <th className="p-3">Data</th>
                  <th className="p-3">Cliente</th>
                  <th className="p-3">Dispositivo</th>
                  <th className="p-3">Matricola</th>
                  <th className="p-3">Modello</th>
                  <th className="p-3">Ultimo intervento</th>
                  <th className="p-3">Indirizzo</th>
                  <th className="p-3">Telefono</th>
                </tr>
              </thead>

              <tbody>
                {eventi.map((e, idx) => (
                  <tr key={`${e.matricola}-${idx}`} className="border-t border-white/10">
                    <td className="p-3 whitespace-nowrap">{formatDateIT(e.data)}</td>
                    <td className="p-3">{e.cliente}</td>
                    <td className="p-3">{e.dispositivo}</td>
                    <td className="p-3">{e.matricola}</td>
                    <td className="p-3">{e.modello}</td>
                    <td className="p-3 whitespace-nowrap">{formatDateIT(e.ultimo)}</td>
                    <td className="p-3 max-w-[360px] truncate" title={e.indirizzo || ""}>
                      {e.indirizzo}
                    </td>
                    <td className="p-3 whitespace-nowrap">{e.telefono}</td>
                  </tr>
                ))}

                {!eventi.length && (
                  <tr>
                    <td className="p-4 text-center text-white/80" colSpan={8}>
                      Nessuna scadenza trovata.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* MOBILE */}
        <div className="md:hidden space-y-3">
          {eventi.map((e, idx) => (
            <div key={`${e.matricola}-${idx}`} className="bg-[#173341] rounded-2xl p-4 text-white">
              <div className="flex items-start justify-between gap-3">
                <div className="font-bold">
                  {e.nome} {e.cognome}
                </div>
                <div className="text-xs bg-[#0f2430] px-2 py-1 rounded-lg whitespace-nowrap">
                  {formatDateIT(e.data)}
                </div>
              </div>

              <div className="mt-2 text-sm space-y-1">
                <div><span className="font-semibold">Dispositivo:</span> {e.dispositivo}</div>
                <div className="break-words"><span className="font-semibold">Matricola:</span> {e.matricola}</div>
                <div className="break-words"><span className="font-semibold">Modello:</span> {e.modello}</div>
                <div><span className="font-semibold">Ultimo intervento:</span> {formatDateIT(e.ultimo) || "-"}</div>
                <div className="break-words"><span className="font-semibold">Indirizzo:</span> {e.indirizzo || "-"}</div>
                <div><span className="font-semibold">Telefono:</span> {e.telefono || "-"}</div>
              </div>
            </div>
          ))}

          {!eventi.length && (
            <div className="bg-[#173341] rounded-2xl p-4 text-white/80 text-center">
              Nessuna scadenza trovata.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
